import { useEffect, useState } from "react";
import { AdminUsage } from "./AdminUsage";
import { apiFetch } from "./viewer";

type Report = {
  fal: { balanceUsd: number | null; currency?: string; configured: boolean; error?: string; minBalanceUsd: number; generationPaused: boolean };
  machgen: { balanceUsd: number | null; minBalanceUsd: number; generationPaused: boolean; pendingTasks: number; runningTasks: number; error?: string };
  gmi: {
    estimatedUsd: number;
    baselineUsd: number;
    baselineAt: string;
    spentSinceBaselineUsd: number;
    llmSpentUsd: number;
    videoSpentUsd: number;
    videoClips: number;
    costedCalls: number;
    minBalanceUsd: number;
    generationPaused: boolean;
  };
  library: { clips: number; reuses: number; saved_usd: number | null } | null;
  /** The admin password is still the public fallback from the open-source repo. */
  publicDefaultPassword: boolean;
  /** Debug database size: it shares the deploy volume with everything else. */
  dbBytes: number;
  storage: {
    enabled: boolean;
    gb: number;
    objects: number;
    freeTierGb: number;
    capGb: number;
    percentOfCap: number;
    projectedMonthlyUsd: number;
    grossMonthlyUsd: number;
    overCap: boolean;
    byKind: { kind: string; gb: number; objects: number; monthlyUsd: number }[];
  };
};

/** Shared with the other password-gated panels so unlocking once unlocks all of them for the session. */
export const PW_KEY = "prison-escape:admin-password";
/** Fired once the password has been accepted by the server, so locked panels can re-render. */
export const ADMIN_UNLOCKED = "slop-prison:admin-unlocked";
export const storedAdminPassword = () => {
  try {
    return sessionStorage.getItem(PW_KEY) ?? "";
  } catch {
    return "";
  }
};

/** The admin password for this session, kept current as other panels unlock it. */
export function useAdminPassword() {
  const [password, setPassword] = useState(storedAdminPassword);
  useEffect(() => {
    const sync = () => setPassword(storedAdminPassword());
    addEventListener(ADMIN_UNLOCKED, sync);
    return () => removeEventListener(ADMIN_UNLOCKED, sync);
  }, []);
  return password;
}

/** Password-gated credit balances. The password is verified server-side against a stored hash. */
export function AdminCredits() {
  const [password, setPassword] = useState(() => {
    try {
      return sessionStorage.getItem(PW_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (pw = password) => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/admin/credits", { method: "POST", body: JSON.stringify({ password: pw }) });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      setReport(body);
      try {
        sessionStorage.setItem(PW_KEY, pw);
      } catch {}
      // The settings controls and the provider panel are locked until a password is known to be good;
      // tell them rather than making each one poll sessionStorage.
      dispatchEvent(new Event(ADMIN_UNLOCKED));
    } catch (err) {
      setReport(null);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Re-open with a remembered password: fetch balances straight away.
  useEffect(() => {
    if (password) load(password);
  }, []);

  if (!report) {
    return (
      <form
        onSubmit={e => {
          e.preventDefault();
          load();
        }}
        className="mb-4 border-b border-white/10 pb-3"
      >
        <div className="text-white/70">Credits (admin password)</div>
        <div className="mt-1 flex gap-2">
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="password"
            className="min-w-0 flex-1 rounded border border-white/15 bg-white/5 px-2 py-1 text-white"
          />
          <button type="submit" disabled={loading || !password} className="rounded bg-teal px-3 py-1 font-semibold text-black disabled:opacity-40">
            {loading ? "…" : "UNLOCK"}
          </button>
        </div>
        {error && <div className="mt-1 text-xs text-siren-red">{error}</div>}
      </form>
    );
  }

  const { machgen, gmi, storage, fal } = report;
  // Fills toward the cap, then turns red once uploads are being refused.
  const barColor = storage.overCap ? "bg-siren-red" : storage.percentOfCap > 75 ? "bg-sodium" : "bg-teal";
  return (
    <div className="mb-4 space-y-2 border-b border-white/10 pb-3">
      {report.publicDefaultPassword && (
        <div className="rounded border border-siren-red/60 bg-siren-red/10 p-2 text-xs text-siren-red">
          <b>Admin password is the public default.</b> It is visible in this open-source repo, so anyone can unlock
          these controls. Set ADMIN_PASSWORD on the server.
        </div>
      )}
      <div className="flex items-center justify-between text-white/70">
        <span>Credits</span>
        <button onClick={() => load()} className="text-xs text-teal hover:underline">
          {loading ? "refreshing…" : "refresh"}
        </button>
      </div>
      <div className="rounded border border-white/10 p-2">
        <div className="flex justify-between">
          <span>fal.ai (video)</span>
          <b className={fal.generationPaused ? "text-siren-red" : fal.balanceUsd === null ? "text-white/40" : "text-[#3dff7a]"}>
            {fal.balanceUsd === null ? (fal.configured ? "unknown" : "—") : `$${fal.balanceUsd.toFixed(2)}`}
          </b>
        </div>
        <div className="text-xs text-white/40">
          {!fal.configured
            ? "Add an Admin-scope fal key (falAdmin / FAL_ADMIN_KEY) to see the balance; a normal key can't read it."
            : fal.generationPaused
              ? `Below $${fal.minBalanceUsd}: video generation paused`
              : `Auto-pauses video below $${fal.minBalanceUsd}`}
          {fal.error && ` · ${fal.error}`}
        </div>
      </div>
      <div className="rounded border border-white/10 p-2">
        <div className="flex justify-between">
          <span>MachGen (video)</span>
          <b className={machgen.generationPaused ? "text-siren-red" : "text-[#3dff7a]"}>
            {machgen.balanceUsd === null ? "unknown" : `$${machgen.balanceUsd.toFixed(2)}`}
          </b>
        </div>
        <div className="text-xs text-white/40">
          {machgen.generationPaused
            ? `Below $${machgen.minBalanceUsd}: video generation paused`
            : `Auto-pauses video below $${machgen.minBalanceUsd}`}
          {machgen.runningTasks + machgen.pendingTasks > 0 && ` · ${machgen.runningTasks + machgen.pendingTasks} tasks in flight`}
          {machgen.error && ` · ${machgen.error}`}
        </div>
      </div>
      {/* Projected Cloudflare usage. Shown whatever the level, so the number is familiar before it matters. */}
      <div className={`rounded border p-2 ${storage.overCap ? "border-siren-red/60 bg-siren-red/10" : "border-white/10"}`}>
        <div className="flex justify-between">
          <span>Cloudflare R2 (storage)</span>
          <b className={storage.overCap ? "text-siren-red" : "text-teal"}>
            {storage.gb.toFixed(2)} / {storage.capGb} GB
          </b>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded bg-white/10">
          <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, storage.percentOfCap)}%` }} />
        </div>
        <div className="mt-1 text-xs text-white/40">
          {storage.objects} objects · projected{" "}
          <b className={storage.projectedMonthlyUsd > 0 ? "text-sodium" : "text-[#3dff7a]"}>
            ${storage.projectedMonthlyUsd.toFixed(2)}/mo
          </b>{" "}
          {storage.projectedMonthlyUsd === 0
            ? `(inside the ${storage.freeTierGb} GB free tier; would be $${storage.grossMonthlyUsd.toFixed(2)} without it)`
            : `(${storage.freeTierGb} GB free tier exceeded)`}
          {!storage.enabled && " · R2 not configured, serving from disk"}
          {` · debug db ${(report.dbBytes / 1e6).toFixed(0)} MB on the volume`}
        </div>
        {storage.byKind.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-white/30">
            {storage.byKind.map(k => (
              <span key={k.kind}>
                {k.kind} {k.gb.toFixed(2)} GB ({k.objects})
              </span>
            ))}
          </div>
        )}
        {storage.overCap && (
          <div className="mt-1 text-xs font-semibold text-siren-red">
            Cap reached — new uploads to Cloudflare are blocked. New clips stay on the container disk until space is freed.
          </div>
        )}
      </div>
      <AdminUsage password={password} />
      {report.library && (
        <div className="rounded border border-white/10 p-2">
          <div className="flex justify-between">
            <span>Action archive</span>
            <b className="text-teal">{report.library.clips} clips</b>
          </div>
          <div className="text-xs text-white/40">
            Reused {report.library.reuses} times · ~${(report.library.saved_usd ?? 0).toFixed(2)} of generation skipped
          </div>
        </div>
      )}
      <div className="rounded border border-white/10 p-2">
        <div className="flex justify-between">
          <span>GMI Cloud (video + LLM)</span>
          <b className={gmi.generationPaused ? "text-siren-red" : "text-sodium"}>≈ ${gmi.estimatedUsd.toFixed(2)}</b>
        </div>
        <div className="text-xs text-white/40">
          Estimate: ${gmi.baselineUsd.toFixed(2)} console balance − ${gmi.videoSpentUsd.toFixed(2)} video ({gmi.videoClips} clips) − $
          {gmi.llmSpentUsd.toFixed(3)} LLM ({gmi.costedCalls} calls). GMI's balance API needs a console login, so update the
          baseline in credits.ts when you check it.
          {gmi.generationPaused && ` Below $${gmi.minBalanceUsd}: GMI video generation paused.`}
        </div>
      </div>
    </div>
  );
}
